import React, { useState, useEffect } from "react";
import {
  AppBar,
  Typography,
  Button,
  TextField,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { SETTING_API_20 } from "../../api/ApiModel.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import {
  CON_STATUS_ACTIVE,
  CON_VALUE_ZERO,
  CON_MAX_NAME_LENGTH,
  CON_MAX_DESCRIPTION_LENGTH,
} from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
// Import messages
import { 
  MainCategoryMessages,
  CategoryMessages,
  getInputMaxLengthMessage,
  getFieldMaxLengthMessage  // Add this import if it exists
} from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface MainCategoryItem {
  catcode?: string | number;
  catname?: string;
  descrip?: string;
  maincatcode?: string | number;
  maincatname?: string;
}

interface FormDetails {
  maincatname: string;
  descrip: string;
  maincatcode: string | number;
}

interface ErrorState {
  maincatname?: string;
  descrip?: string;
  [key: string]: string | undefined;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  USERNAME?: string;
  COMPCODE?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface LocationState {
  selectedItem?: MainCategoryItem;
  actionText?: string;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

// UI Text constants (since these don't exist in Messages.tsx)
const UI_TEXT = {
  breadcrumb: {
    home: "Home",
    settings: "Settings",
    mainCategory: "Main Category",
  },
  pageTitle: {
    create: "Create Main Category",
    edit: "Edit Main Category",
  },
  button: {
    cancel: "Cancel",
    confirm: "Confirm",
    modify: "Modify",
  },
  tooltip: {
    back: "Back to All Items",
  },
  label: {
    mainCategoryName: "Main Category Name",
    description: "Description",
  },
};

function CreateMainCategory(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  const location = useLocation();
  const { selectedItem } = (location.state as LocationState) || {};

  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { LOCCODE, STAFFMAPLOCODE, USERNAME, COMPCODE } =
    SYS_USER.USER_PARAM || {};
  const isEdit = (location.state as LocationState)?.actionText === "Edit";

  // State
  const [data, setData] = useState<MainCategoryItem[]>([]);
  const [error, setError] = useState<ErrorState>({});
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const [alertMessage, setAlertMessage] = useState<string>("");

  const [formDetails, setFormDetails] = useState<FormDetails>({
    maincatname: "",
    descrip: "",
    maincatcode: CON_VALUE_ZERO,
  });

  const goBackToView = (): void => navigate("/vMainCategory");

  // ---------- categories ----------
  const fetchCategories = async (): Promise<void> => {
    try {
      const result = await externalgetApi(SETTING_API_20, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
      });
      setData(result as MainCategoryItem[]);
    } catch (err) {
      console.error("Fetch Error:", err);
      setAlertType("error");
      setAlertMessage(CategoryMessages.error.fetchFailed);
      setAlertOpen(true);
    }
  };

  // ----------useEffects ----------
  useEffect(() => {
    if (selectedItem) {
      setFormDetails({
        maincatname: selectedItem.catname || "",
        descrip: selectedItem.descrip || "",
        maincatcode: selectedItem.catcode || CON_VALUE_ZERO,
      });
    }
  }, [selectedItem]);

  useEffect(() => {
    setPageData({ title: UI_TEXT.breadcrumb.mainCategory } as PageData);
  }, [setPageData]);

  useEffect(() => {
    fetchCategories();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Handlers ----------
  // Main Category Name input change handler
  const handleMainCatNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    
    // Remove leading spaces completely
    const noLeadingSpaceValue = value.replace(/^\s+/, "");
    
    let errorMsg = "";
    
    // Check if max length reached
    if (noLeadingSpaceValue.length === CON_MAX_NAME_LENGTH) {
      // Option 1: If getInputMaxLengthMessage expects only maxLength
      errorMsg = getInputMaxLengthMessage(CON_MAX_NAME_LENGTH);
      
      // Option 2: If you want field-specific message, create a custom message
      // errorMsg = `${UI_TEXT.label.mainCategoryName} cannot exceed ${CON_MAX_NAME_LENGTH} characters.`;
    }

    setError((prev) => ({ ...prev, [name]: errorMsg }));
    setFormDetails((prev) => ({
      ...prev,
      [name]: noLeadingSpaceValue,
    }));
  };

  // Description input change handler
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;

    // Remove leading spaces completely
    const noLeadingSpaceValue = value.replace(/^\s+/, "");

    let errorMsg = "";
    
    // Check if max length reached
    if (noLeadingSpaceValue.length === CON_MAX_DESCRIPTION_LENGTH) {
      // Option 1: If getInputMaxLengthMessage expects only maxLength
      errorMsg = getInputMaxLengthMessage(CON_MAX_DESCRIPTION_LENGTH);
      
      // Option 2: If you want field-specific message, create a custom message
      // errorMsg = `${UI_TEXT.label.description} cannot exceed ${CON_MAX_DESCRIPTION_LENGTH} characters.`;
    }

    setError((prev) => ({ ...prev, [name]: errorMsg }));
    setFormDetails((prev) => ({
      ...prev,
      [name]: noLeadingSpaceValue,
    }));
  };

  // Handle Cancel
  const handleCancel = (): void => {
    // Clear any error messages and navigate back to the previous page
    setError({});
    navigate("/vMainCategory");
  };
  
  // Handle Confirm button
  const handleConfirmClick = async (): Promise<void> => {
    setIsButtonDisabled(true);

    const { maincatname, maincatcode, descrip } = formDetails;
    const trimmedName = (maincatname || "").trim();
    const trimmedDesc = (descrip || "").trim();

    let validationErrors: ErrorState = {};
    let hasError = false;

    // Required field validation
    if (!trimmedName) {
      validationErrors.maincatname = MainCategoryMessages.warning.nameRequired;
      hasError = true;
    }

    setError(validationErrors);

    if (hasError) {
      setAlertType("error");
      setAlertMessage(MainCategoryMessages.warning.requiredFields);
      setAlertOpen(true);
      setIsButtonDisabled(false);
      return;
    }

    // Check for duplicate category names
    const isDuplicate = data.some(
      (item) =>
        item.maincatcode !== maincatcode &&
        item.maincatname?.trim().toLowerCase() === trimmedName.toLowerCase()
    );

    if (isDuplicate) {
      setAlertType("warning");
      setAlertMessage(MainCategoryMessages.warning.alreadyExists);
      setAlertOpen(true);
      setIsButtonDisabled(false);
      return;
    }

    // Prepare payload
    const payload = {
      maincatcode: maincatcode || CON_VALUE_ZERO,
      compcode: COMPCODE,
      loccode: STAFFMAPLOCODE,
      maincatname: trimmedName,
      descrip: trimmedDesc,
      sts: CON_STATUS_ACTIVE,
      enuser: USERNAME,
      endate: new Date().toLocaleDateString("en-GB"),
      MODE: isEdit ? "EDIT" : "CREATE",
    };

    // Call create/update API
    try {
      await externalpostApi(SETTING_API_20, payload);
      setAlertType("success");
      setAlertMessage(
        isEdit ? MainCategoryMessages.success.update : MainCategoryMessages.success.create
      );
      setAlertOpen(true);
      setTimeout(() => navigate("/vMainCategory"), 1500);
    } catch (err) {
      const error = err as ApiError;
      console.error("API Error:", error);
      setAlertType("error");
      setAlertMessage(
        error.response?.data?.message || MainCategoryMessages.error.unexpected
      );
      setAlertOpen(true);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  return (
    <>
      <Box sx={styles.box_breadcrumb}>
        <Tooltip title={UI_TEXT.tooltip.back} arrow sx={styles.breadcrumb_tooltip}>
          <IconButton
            id="btn_backtoview"
            aria-label="back"
            sx={styles.btn_breadcrumb_back}
            onClick={goBackToView}
          >
            <ArrowBackIcon
              id="ico_backtoview"
              sx={styles.btn_breadcrumb_back_icon}
            />
          </IconButton>
        </Tooltip>
        <Typography sx={styles.breadcrumb_divider}>|</Typography>

        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link underline="hover" sx={styles.breadcrumb_link}>
            {UI_TEXT.breadcrumb.home}
          </Link>
          <Link sx={styles.breadcrumb_link} underline="hover">
            {UI_TEXT.breadcrumb.settings}
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            aria-current="page"
          >
            {UI_TEXT.breadcrumb.mainCategory}
          </Link>
        </Breadcrumbs>
      </Box>

      {/* Main Container */}
      <Box sx={styles.box_add_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 8, sm: 10 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>
                {isEdit ? UI_TEXT.pageTitle.edit : UI_TEXT.pageTitle.create}
              </Typography>
            </Grid>
          </Grid>
        </AppBar>

        <Box
          component="form"
          noValidate
          autoComplete="off"
          sx={{
            ...styles.add_form_page_container,
            maxWidth: "500px",
            marginTop: "20px",
            padding: 1,
          }}
        >
          {/* Form Fields */}
          <Grid container spacing={2} direction="column">
            <Grid size={{ xs: 12 }}>
              <TextField
                id="txt_maincatname"
                label={UI_TEXT.label.mainCategoryName}
                variant="outlined"
                fullWidth
                size="small"
                required
                name="maincatname"
                value={formDetails.maincatname}
                onChange={handleMainCatNameChange}
                error={!!error.maincatname}
                helperText={error.maincatname}
                FormHelperTextProps={{ sx: { fontSize: "12px", color: "red" } }}
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: false,
                  maxLength: CON_MAX_NAME_LENGTH,
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                id="txt_descrip"
                label={UI_TEXT.label.description}
                variant="outlined"
                fullWidth
                size="small"
                name="descrip"
                value={formDetails.descrip}
                onChange={handleDescriptionChange}
                error={!!error.descrip}
                helperText={error.descrip}
                FormHelperTextProps={{ sx: { fontSize: "12px", color: "red" } }}
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: false,
                  maxLength: CON_MAX_DESCRIPTION_LENGTH,
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Action Buttons */}
        <Box sx={styles.add_form_page_button_box}>
          <Button
            id="btn_cancel"
            variant="outlined"
            size="medium"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            onClick={handleCancel}
          >
            {UI_TEXT.button.cancel}
          </Button>
          <Button
            id="btn_confirm/modify"
            variant="contained"
            size="medium"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            onClick={handleConfirmClick}
            disabled={isButtonDisabled}
          >
            {isEdit ? UI_TEXT.button.modify : UI_TEXT.button.confirm}
          </Button>
        </Box>

        <SystemAlert
          open={alertOpen}
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />
      </Box>
    </>
  );
}

export default CreateMainCategory;